'use client';

import { useState, useEffect } from 'react';
import { Navigation } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { detectLanguageFromGPS, detectFromIP } from '@/lib/geo-language';

const STORAGE_KEY_GEO_ASKED = 'love_languages_geo_asked';

export function GeoModal() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const hasAskedGeo = localStorage.getItem(STORAGE_KEY_GEO_ASKED);
    if (!hasAskedGeo) {
      const timer = setTimeout(() => setShowModal(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const changeLocale = (newLocale: string) => {
    if (newLocale !== locale) {
      const currentPath = pathname.replace(`/${locale}`, '') || '/';
      router.push(`/${newLocale}${currentPath}`);
    }
  };

  const handleGeoAllow = async () => {
    setShowModal(false);
    localStorage.setItem(STORAGE_KEY_GEO_ASKED, 'true');

    const lang = await detectLanguageFromGPS();
    if (lang) {
      changeLocale(lang);
    } else {
      const ipLang = await detectFromIP();
      if (ipLang) changeLocale(ipLang);
    }
  };

  const handleGeoDeny = async () => {
    setShowModal(false);
    localStorage.setItem(STORAGE_KEY_GEO_ASKED, 'true');

    const lang = await detectFromIP();
    if (lang) {
      changeLocale(lang);
    }
  };

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/90 backdrop-blur-md"
      data-testid="geo-modal-overlay"
    >
      <div
        className="bg-card border border-border card-elevated rounded-3xl p-8 max-w-sm w-full flex flex-col items-center text-center space-y-5 animate-scale-in"
        data-testid="geo-modal-card"
      >
        <div
          className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary"
          data-testid="geo-modal-icon"
        >
          <Navigation className="w-7 h-7" />
        </div>
        <h2
          className="font-display text-2xl font-semibold text-foreground"
          data-testid="geo-modal-title"
        >
          {t('geo_modal_title')}
        </h2>
        <p
          className="text-muted-foreground text-sm leading-relaxed"
          data-testid="geo-modal-desc"
        >
          {t('geo_modal_desc')}
        </p>
        <div className="flex gap-3 w-full mt-2">
          <button
            onClick={handleGeoDeny}
            className="flex-1 px-4 py-3 rounded-xl border border-border text-foreground hover:bg-secondary font-semibold transition-all text-sm cursor-pointer"
            data-testid="geo-modal-deny"
          >
            {t('geo_deny')}
          </button>
          <button
            onClick={handleGeoAllow}
            className="flex-1 px-4 py-3 rounded-xl btn-primary text-primary-foreground font-semibold text-sm cursor-pointer"
            data-testid="geo-modal-allow"
          >
            {t('geo_allow')}
          </button>
        </div>
      </div>
    </div>
  );
}
